import axios from 'axios';
import { USER_ROLE } from 'constants/common';
import http from './http';

const apiBase = () => (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const partnerServices = {
  /** Public — no JWT. GET /api/partner/invite/validate?token= */
  async validateInviteToken(token: string): Promise<any> {
    try {
      const res = await axios.get(`${apiBase()}/api/partner/invite/validate`, {
        params: { token },
        headers: { 'Content-Type': 'application/json' },
        validateStatus: () => true,
      });
      return res.data;
    } catch {
      return { valid: false, message: 'Could not verify invitation.' };
    }
  },

  /** Authenticated — POST /api/partner/invite/accept */
  async acceptPartnerInvite(invite_token: string): Promise<any> {
    const res = await http.post('api/partner/invite/accept', { invite_token });
    return res;
  },

  async createPartner(body: any): Promise<any> {
    const res = await http.post('api/partner', body);
    return res;
  },

  async inviteCounselor(student_id: any, email: any): Promise<any> {
    let postData = {
      student_id: student_id,
      email: email,
    };

    const res = await http.post('api/partner/invite-counselor', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },

  async student_inviteParent(
    student_id: any,
    email: any,
    first_name?: string,
    last_name?: string,
    relationship?: string
  ): Promise<any> {
    let postData: Record<string, any> = {
      student_id: student_id,
      email: email,
    };
    if (first_name != null && first_name !== '') postData.first_name = first_name;
    if (last_name != null && last_name !== '') postData.last_name = last_name;
    const rel = relationship != null ? String(relationship).trim() : '';
    if (rel !== '') postData.relation = rel;

    const res = await http.post('api/partner/student/invite-parent', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },

  async parent_InviteStudent(
    parentId: any,
    studentEmail: any,
    first_name?: string,
    last_name?: string
  ): Promise<any> {
    let postData: Record<string, any> = {
      parent_id: parentId,
      student_email: studentEmail,
    };
    if (first_name != null && first_name !== '') postData.first_name = first_name;
    if (last_name != null && last_name !== '') postData.last_name = last_name;

    const res = await http.post('api/partner/parent/invite-parent', postData);
    return res;
  },


  async inviteStudent(parent_id: any, student_email: any): Promise<any> {
    let postData = {
      parent_id: parent_id,
      student_email: student_email
    };

    const res = await http.post('api/partner/parent-invite-student', postData);
    return res;
  },

  async getCurrentCounselor(student_id: any): Promise<any> {
    let postData = {
      student_id: student_id,
    };

    const res = await http.post('api/partner/get-current-counselor', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },

  async getCurrentParent(student_id: any): Promise<any> {
    let postData = {
      student_id: student_id,
    };

    const res = await http.post('api/partner/get-parents', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },


  async getInvitePartnerList(student_id: any): Promise<any> {
    let postData = {
      partner_id: student_id,
    };

    const res = await http.post('api/partner/get-invite-partner-list', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },

  
  async parentGetInvitePartnerList(parent_id: any): Promise<any> {
    let postData = {
      parent_id: parent_id,
    };

    const res = await http.post('api/partner/parent/get-invite-list', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },

  async studentGetInvitePartnerList(student_id: any): Promise<any> {
    let postData = {
      student_id: student_id,
    };

    const res = await http.post('api/partner/student/get-invite-list', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },

  /** Student: outgoing email invites (e.g. pending parent invites). GET /api/partner/get-my-outgoing-partner-email-invites */
  async getMyOutgoingPartnerEmailInvites(): Promise<{
    invite_kind?: string;
    invites?: any[];
  }> {
    const res: any = await http.post('api/partner/get-outgoing-partner-email-invites');
    return res ?? { invites: [] };
  },

  /** POST /api/partner/cancel-outgoing-partner-email-invite — body: { invite_id } */
  async cancelOutgoingPartnerEmailInvite(invite_id: number): Promise<any> {
    const res = await http.post(
      'api/partner/cancel-outgoing-partner-email-invite',
      { invite_id },
      {
        headers: {
          'content-type': 'application/json;charset=UTF-8',
          'Access-Control-Allow-Origin': '*',
        },
      }
    );
    return res;
  },

  /** POST /api/partner/resend-outgoing-partner-email-invite — body: { invite_id } */
  async resendOutgoingPartnerEmailInvite(invite_id: number): Promise<any> {
    const res = await http.post(
      'api/partner/resend-outgoing-partner-email-invite',
      { invite_id },
      {
        headers: {
          'content-type': 'application/json;charset=UTF-8',
          'Access-Control-Allow-Origin': '*',
        },
      }
    );
    return res;
  },

  async updateCurrentCounselor(body: any): Promise<any> {
    const res = await http.put('api/partner', body);
    return res;
  },

  /**
   * Single endpoint for all invite status updates (replaces old update-student- vs update-partner-invited-status split).
   * @param student_id - Student’s user id in the relationship
   * @param partner_id - Other party’s user id (e.g. parent, counselor, or student depending on who accepted)
   */
  async updatePartnerInviteStatus(student_id: any, partner_id: any, status: string): Promise<any> {
    const postData = {
      student_id: student_id,
      partner_id: partner_id,
      status: status,
    };

    const res = await http.post('api/partner/update-student-invited-status', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },
  
  async setDefaultParent(student_id:number , parent_id:number) : Promise<any> {
    let postData = {
      student_id: student_id,
      parent_id: parent_id,
    };

    const res = await http.post('api/students/set-current-parent', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },
  async sync(email: string): Promise<any> {
    let postData = {
      email: email,
    };

    const res = await http.post('api/webhook/sync', postData, {
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
      },
    });
    return res;
  },

  
  async getCounselors(studentId: any): Promise<any> {
    const res: any = await http.post(`api/partner/get-all-partners`, {
      is_show_partner_information: true,
      partner_role: USER_ROLE.counselor,
      student_id: parseInt(studentId),
    });
    const result = res.filter((item: any) => item.requested_status === 'approved');
    return result;
  },

  async getParents(studentId: any): Promise<any> {
    const res: any = await http.post(`api/partner/get-all-partners`, {
      is_show_partner_information: true,
      partner_role: USER_ROLE.parent,
      student_id: parseInt(studentId),
    });
    const result = res.filter((item: any) => item.requested_status === 'approved');
    return result;
  },

  /** Student role: POST api/partner/get-partner-by-email (e.g. invite parent). Parent flows use getStudentByEmail. */
  async getPartnerByEmail(email: any): Promise<any> {
    let arr:any[] = await http.post(`api/partner/get-partner-by-email`, {      
      email: email,
    });

    if (arr && arr.length>0) {
      return arr[0];
    }
    return null;    
  },


  async makeTokenUsed(body: { invite_token: string; status: string }): Promise<any> {
    const res = await http.post('api/partner/invite/make-token-used', body);
    return res;
  },

  /** Parent role — resolve a student record by email (GET /api/students/email/:email). */
  async getStudentByEmail(email: string): Promise<any> {
    const trimmed = (email ?? '').trim();
    if (!trimmed) return null;
    try {
      const res: any = await http.get(`api/students/email/${encodeURIComponent(trimmed)}`);
      const data = res?.result !== undefined ? res.result : res;
      if (Array.isArray(data)) {
        return data.length > 0 ? data[0] : null;
      }
      if (data && typeof data === 'object' && data.data !== undefined) {
        return data.data;
      }
      return data ?? null;
    } catch {
      return null;
    }
  },

};

export default partnerServices;
